import { useCallback, useState } from 'react';
import sendExperienceAnalytics from '@atlassian/jira-common-experience-tracking-analytics/src/index.tsx';
import logger from '@atlassian/jira-common-util-logging/src/log.tsx';
import { ADD_GADGET_EXPERIENCE } from '@atlassian/jira-dashboard-common/src/constants.tsx';
import type { GadgetDirectoryItemType } from '@atlassian/jira-dashboard-common/src/types.tsx';
import { mapToReactGadgets } from '@atlassian/jira-dashboard-common/src/utils/map-to-react-gadgets.tsx';
import { lensGadgetsList } from '@atlassian/jira-dashboard-internal-common/src/utils/dashboard-config-lenses/index.tsx';
import { handleFetchErrorAsExperience } from '@atlassian/jira-dashboard-internal-common/src/utils/handle-fetch-error-as-experience/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService, type FlagConfiguration } from '@atlassian/jira-flags';
import { toFlagId } from '@atlassian/jira-flags/src/services/types';
import { useDashboardResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';
import {
	PACKAGE_NAME,
	IGNORED_GADGETS_FROM_COUNT_MAP,
	MAX_ALLOWED_GADGETS_COUNT,
	MAX_GADGETS_COUNT_INCLUDE_IGNORED_GADGETS,
} from '../../constants.tsx';
import { useRightPanelState } from '../../controllers/right-panel-layout-controller.tsx';
import { useAddGadgetWithinLimit } from '../../utils/add-gadget-within-limit/index.tsx';
import messages from './messages.tsx';

export const expAttributes = {
	analyticsSource: 'dashboard',
	experience: ADD_GADGET_EXPERIENCE,
	application: null,
	edition: null,
	additionalAttributes: {},
} as const;

const errorFlagAttributes: FlagConfiguration = {
	key: toFlagId('gadgetAddError'),
	type: 'error',
	title: messages.errorFlagTitle,
	description: messages.errorFlagDescription,
	messageId: 'dashboard-edit.services.add-gadget.error-flag-attributes.error',
	messageType: 'transactional',

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	actions: [{ content: messages.errorFlagAction, onClick: () => window.location.reload() }],
};

export const useAddGadget = ({
	onBeforeGadgetAdded,
}: {
	onBeforeGadgetAdded?: (id: string) => void;
}) => {
	const { data, update } = useDashboardResource();
	const [addGadgetLoadingId, setAddGadgetLoadingId] = useState<string | null>(null);
	const { showFlag } = useFlagsService();
	const { panelId } = useRightPanelState();
	const { canAddGadgetWithinLimit, showGadgetLimitFlag } = useAddGadgetWithinLimit(
		MAX_GADGETS_COUNT_INCLUDE_IGNORED_GADGETS,
		MAX_ALLOWED_GADGETS_COUNT,
	);
	const columnIndex = panelId?.slice(panelId.length - 1);

	const addGadget = useCallback(
		(gadget: GadgetDirectoryItemType) => {
			if (data == null) return;

			const isGadgetIgnored = Object.keys(IGNORED_GADGETS_FROM_COUNT_MAP).includes(gadget.id);
			if (!canAddGadgetWithinLimit(data.gadgets, isGadgetIgnored)) {
				showGadgetLimitFlag();
				return;
			}

			setAddGadgetLoadingId(gadget.id);

			const dashboardResourceUrl = data.resourceUrl;
			const type =
				(gadget.specUri != null && gadget.specUri.trim().length > 0 && 'gadget') ||
				(gadget.isForgeGadget && 'forge-gadget') ||
				'dashboard-item';
			const dashboardItemId =
				(gadget.specUri != null && gadget.specUri.trim().length > 0 && gadget.specUri) ||
				(gadget.isForgeGadget && gadget.id) ||
				gadget.completeModuleKey;
			// see https://stash.atlassian.com/projects/JIRACLOUD/repos/jira/commits/c93a98012d73ede4496464312f4fdda1f73abde3#dashboard-plugin/src/main/resources/js/dashboard-service.js
			// verified that localeLang and localeCountry are no longer needed
			performPostRequest(`${dashboardResourceUrl}.json`, {
				body: JSON.stringify({
					dashboardItemId,
					type,
					columnIndex,
				}),
			})
				.then((gadgetData) => {
					onBeforeGadgetAdded?.(gadgetData.id);
					update(lensGadgetsList((gadgets) => [...mapToReactGadgets([gadgetData]), ...gadgets]));
					sendExperienceAnalytics({
						...expAttributes,
						wasExperienceSuccesful: true,
					});
					logger.safeInfoWithoutCustomerData('spa-apps.dashboard', 'Add gadget');
				})
				.catch((error) => {
					if (
						error.errors?.length > 0 &&
						error.errors[0]?.field === 'columnIndex' &&
						error.errors[0]?.error
					) {
						const invalidColumnFlagAttributes: FlagConfiguration = {
							key: toFlagId('gadgetAddError'),
							type: 'error',
							title: messages.invalidColumnFlagTitle,
							description: error.errors[0].error,
							messageId: 'dashboard-edit.services.add-gadget.invalid-column-flag-attributes.error',
							messageType: 'transactional',
							actions: [
								{
									content: messages.errorFlagAction,

									// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
									onClick: () => window.location.reload(),
								},
							],
						};
						showFlag(invalidColumnFlagAttributes);
					} else {
						showFlag(errorFlagAttributes);
					}

					handleFetchErrorAsExperience({
						error,
						sendSuccessExperience: () => {
							sendExperienceAnalytics({
								...expAttributes,
								wasExperienceSuccesful: true,
							});
						},
						sendFailedExperience: () => {
							sendExperienceAnalytics({
								...expAttributes,
								wasExperienceSuccesful: false,
							});
						},
						logErrorEvent: (reason) => {
							fireErrorAnalytics({
								error,
								meta: {
									id: ADD_GADGET_EXPERIENCE,
									packageName: PACKAGE_NAME,
								},
								attributes: {
									reason,
									endpoint: 'addGadget',
								},
								sendToPrivacyUnsafeSplunk: true,
							});
						},
					});
				})
				.finally(() => {
					setAddGadgetLoadingId(null);
				});
		},
		[
			data,
			columnIndex,
			canAddGadgetWithinLimit,
			showGadgetLimitFlag,
			showFlag,
			onBeforeGadgetAdded,
			update,
		],
	);

	const getIsLoading = useCallback((id: string) => id === addGadgetLoadingId, [addGadgetLoadingId]);

	const getIsDisabled = useCallback(
		(id: string) => addGadgetLoadingId !== null && id !== addGadgetLoadingId,
		[addGadgetLoadingId],
	);

	return { addGadget, getIsLoading, getIsDisabled };
};
